import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Sparkles, Image as ImageIcon, Save, Download, Edit2, Plus, 
  Palette, Type, Square, Upload, Wand2, Layout, X,
  Loader2, Search, Grid3x3, ChevronLeft, Check, Trash2, Move
} from 'lucide-react';
import { getAuthHeaders } from '../lib/auth';
import Navigation from '../components/Navigation';
import html2canvas from 'html2canvas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DigiPosterCreator = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);

  // State
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('templates');
  const [profile, setProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [platforms, setPlatforms] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [myPosters, setMyPosters] = useState([]);

  // Canvas state
  const [canvasElements, setCanvasElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [editingText, setEditingText] = useState(null);

  // AI & Images
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPlatform, setAiPlatform] = useState('instagram');
  const [aiTone, setAiTone] = useState('professional');
  const [showImageSearch, setShowImageSearch] = useState(false);
  const [imageSearchQuery, setImageSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Profile form
  const [profileForm, setProfileForm] = useState({
    businessName: '',
    ownerName: '',
    email: '',
    phone: '',
    location: '',
    website: '',
    logoSquareUrl: '',
    logoRoundUrl: '',
    businessCategory: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load templates (no auth required) - this should always work
      const templatesRes = await axios.get(`${API}/poster/templates`);
      setTemplates(templatesRes.data.templates || []);
      setPlatforms(templatesRes.data.platforms || {});

      // Try to load profile (with auth)
      try {
        const profileRes = await axios.get(`${API}/poster/profile`, { headers: getAuthHeaders() });
        if (profileRes.data.profile) {
          setProfile(profileRes.data.profile);
          setProfileForm(profileRes.data.profile);
        } else {
          setShowProfileModal(true);
        }
      } catch (profileError) {
        console.log('Profile not loaded, will prompt user');
        setShowProfileModal(true);
      }

      // Try to load posters (with auth)
      try {
        const postersRes = await axios.get(`${API}/poster/my-posters`, { headers: getAuthHeaders() });
        setMyPosters(postersRes.data.posters || []);
      } catch (postersError) {
        console.log('Posters not loaded');
        setMyPosters([]);
      }

    } catch (error) {
      console.error('Critical load error:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/poster/profile`,
        profileForm,
        { headers: getAuthHeaders() }
      );
      setProfile(response.data.profile);
      setShowProfileModal(false);
      toast.success('Business profile saved!');
    } catch (error) {
      toast.error('Failed to save profile');
    }
  };

  const handleLogoUpload = async (e, logoType) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/poster/upload-logo?logo_type=${logoType}`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const field = logoType === 'square' ? 'logoSquareUrl' : 'logoRoundUrl';
      setProfileForm({ ...profileForm, [field]: response.data.url });
      toast.success(`${logoType} logo uploaded!`);
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const selectTemplate = async (template) => {
    setSelectedTemplate(template);
    
    // Create initial canvas elements with business info pre-filled
    const elements = [];
    
    // Get occasion-specific content and background image
    const getOccasionContent = () => {
      switch(template.category) {
        case 'birthday':
          return { 
            main: '🎂 Happy Birthday! 🎉', 
            sub: 'Celebrating Special Moments',
            bgImage: 'https://images.pexels.com/photos/1543762/pexels-photo-1543762.jpeg?auto=compress&cs=tinysrgb&w=1080'
          };
        case 'anniversary':
          return { 
            main: '💐 Happy Anniversary! 🎊', 
            sub: 'Years of Excellence',
            bgImage: 'https://images.pexels.com/photos/1024993/pexels-photo-1024993.jpeg?auto=compress&cs=tinysrgb&w=1080'
          };
        case 'christmas':
          return { 
            main: '🎄 Merry Christmas! 🎅', 
            sub: 'Season\'s Greetings',
            bgImage: 'https://images.pexels.com/photos/1649888/pexels-photo-1649888.jpeg?auto=compress&cs=tinysrgb&w=1080'
          };
        case 'newyear':
          return { 
            main: '🎆 Happy New Year! ✨', 
            sub: '2025 - New Beginnings',
            bgImage: 'https://images.pexels.com/photos/1387037/pexels-photo-1387037.jpeg?auto=compress&cs=tinysrgb&w=1080'
          };
        case 'thanksgiving':
          return { 
            main: '🦃 Happy Thanksgiving! 🍂', 
            sub: 'Grateful for You',
            bgImage: 'https://images.pexels.com/photos/3714458/pexels-photo-3714458.jpeg?auto=compress&cs=tinysrgb&w=1080'
          };
        case 'valentine':
          return { 
            main: '💝 Happy Valentine\'s Day! 💕', 
            sub: 'Spread the Love',
            bgImage: 'https://images.pexels.com/photos/3946752/pexels-photo-3946752.jpeg?auto=compress&cs=tinysrgb&w=1080'
          };
        case 'mothers':
          return { 
            main: '🌸 Happy Mother\'s Day! 💐', 
            sub: 'Celebrating All Moms',
            bgImage: 'https://images.pexels.com/photos/1415558/pexels-photo-1415558.jpeg?auto=compress&cs=tinysrgb&w=1080'
          };
        case 'fathers':
          return { 
            main: '👔 Happy Father\'s Day! 🎁', 
            sub: 'Honoring All Dads',
            bgImage: 'https://images.pexels.com/photos/1325771/pexels-photo-1325771.jpeg?auto=compress&cs=tinysrgb&w=1080'
          };
        case 'easter':
          return { 
            main: '🐰 Happy Easter! 🌷', 
            sub: 'Spring Celebration',
            bgImage: 'https://images.pexels.com/photos/1071878/pexels-photo-1071878.jpeg?auto=compress&cs=tinysrgb&w=1080'
          };
        case 'sale':
          return { 
            main: '🔥 HUGE SALE! 🛍️', 
            sub: 'Limited Time Offer',
            bgImage: 'https://images.pexels.com/photos/1148957/pexels-photo-1148957.jpeg?auto=compress&cs=tinysrgb&w=1080'
          };
        case 'opening':
          return { 
            main: '🎉 Grand Opening! 🎊', 
            sub: 'Join Us for the Celebration',
            bgImage: 'https://images.pexels.com/photos/1266808/pexels-photo-1266808.jpeg?auto=compress&cs=tinysrgb&w=1080'
          };
        default:
          return { 
            main: profile?.businessName || 'Your Business', 
            sub: '✨ Special Offer ✨',
            bgImage: 'https://images.pexels.com/photos/1939485/pexels-photo-1939485.jpeg?auto=compress&cs=tinysrgb&w=1080'
          };
      }
    };
    
    const content = getOccasionContent();
    
    // Add background with image
    elements.push({
      id: 'bg',
      type: 'background',
      color: template.bgColor || '#6366f1',
      gradient: template.bgGradient || { from: '#6366f1', to: '#8b5cf6' },
      image: content.bgImage
    });

    // Add semi-transparent overlay for better text readability
    elements.push({
      id: 'overlay',
      type: 'overlay',
      color: 'rgba(0, 0, 0, 0.4)'
    });

    // Add logo if available - top center
    if (profile?.logoSquareUrl) {
      elements.push({
        id: `logo-${Date.now()}`,
        type: 'image',
        url: profile.logoSquareUrl,
        x: (template.width / 2) - 60,
        y: 60,
        width: 120,
        height: 120
      });
    }

    // Add main heading - FIXED POSITIONING
    elements.push({
      id: `text-${Date.now()}-1`,
      type: 'text',
      content: content.main,
      x: 0,
      y: template.height * 0.35,
      fontSize: 48,
      fontWeight: 'bold',
      color: '#ffffff',
      align: 'center',
      width: template.width
    });

    // Add business name - FIXED POSITIONING
    elements.push({
      id: `text-${Date.now()}-2`,
      type: 'text',
      content: profile?.businessName || 'Your Business Name',
      x: 0,
      y: template.height * 0.55,
      fontSize: 42,
      fontWeight: 'bold',
      color: '#ffffff',
      align: 'center',
      width: template.width
    });

    // Add subheading - FIXED POSITIONING
    elements.push({
      id: `text-${Date.now()}-3`,
      type: 'text',
      content: content.sub,
      x: 0,
      y: template.height * 0.7,
      fontSize: 28,
      color: '#ffffff',
      align: 'center',
      width: template.width
    });

    // Add contact info at bottom - FIXED POSITIONING
    const contactInfo = [];
    if (profile?.phone) contactInfo.push(`📞 ${profile.phone}`);
    if (profile?.website) contactInfo.push(`🌐 ${profile.website}`);
    
    if (contactInfo.length > 0) {
      elements.push({
        id: `text-${Date.now()}-4`,
        type: 'text',
        content: contactInfo.join(' • '),
        x: 0,
        y: template.height - 80,
        fontSize: 16,
        color: '#ffffff',
        align: 'center',
        width: template.width
      });
    }

    setCanvasElements(elements);
    setView('editor');
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setAiGenerating(true);
    try {
      const response = await axios.post(
        `${API}/poster/ai/generate-content`,
        {
          prompt: aiPrompt,
          platform: aiPlatform,
          tone: aiTone
        },
        { headers: getAuthHeaders() }
      );

      // Add AI-generated text to canvas
      const newElement = {
        id: `text-${Date.now()}`,
        type: 'text',
        content: response.data.content,
        x: selectedTemplate.width / 2,
        y: selectedTemplate.height * 0.4,
        fontSize: 42,
        fontWeight: 'bold',
        color: '#ffffff',
        align: 'center',
        width: selectedTemplate.width * 0.8
      };

      setCanvasElements([...canvasElements, newElement]);
      toast.success('AI content added to canvas!');
      setShowAIPanel(false);
    } catch (error) {
      toast.error('Failed to generate content');
    } finally {
      setAiGenerating(false);
    }
  };

  const searchStockImages = async () => {
    if (!imageSearchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(
        `${API}/slides/search-images?query=${encodeURIComponent(imageSearchQuery)}&count=12`
      );
      setSearchResults(response.data.images || []);
    } catch (error) {
      toast.error('Failed to search images');
    } finally {
      setSearching(false);
    }
  };

  const addImageToCanvas = (imageUrl) => {
    const newElement = {
      id: `image-${Date.now()}`,
      type: 'image',
      url: imageUrl,
      x: selectedTemplate.width / 2 - 150,
      y: selectedTemplate.height / 2 - 150,
      width: 300,
      height: 300
    };
    setCanvasElements([...canvasElements, newElement]);
    toast.success('Image added to canvas!');
    setShowImageSearch(false);
  };

  const updateElementText = (elementId, newContent) => {
    setCanvasElements(canvasElements.map(el => 
      el.id === elementId ? { ...el, content: newContent } : el
    ));
  };

  const deleteElement = (elementId) => {
    setCanvasElements(canvasElements.filter(el => el.id !== elementId));
    setSelectedElement(null);
  };

  const addTextElement = () => {
    const newElement = {
      id: `text-${Date.now()}`,
      type: 'text',
      content: 'Double click to edit',
      x: selectedTemplate.width / 2,
      y: selectedTemplate.height / 2,
      fontSize: 32,
      color: '#ffffff',
      align: 'center',
      width: selectedTemplate.width * 0.6
    };
    setCanvasElements([...canvasElements, newElement]);
  };

  const savePoster = async () => {
    const title = prompt('Enter a title for this poster:');
    if (!title) return;

    try {
      await axios.post(
        `${API}/poster/save`,
        {
          title,
          templateId: selectedTemplate?.id,
          canvasData: { elements: canvasElements },
          platform: selectedTemplate?.platform
        },
        { headers: getAuthHeaders() }
      );

      toast.success('Poster saved!');
      loadData();
    } catch (error) {
      toast.error('Failed to save poster');
    }
  };

  const exportPoster = async () => {
    if (!canvasRef.current) return;

    try {
      const canvas = await html2canvas(canvasRef.current, {
        backgroundColor: null,
        scale: 2
      });

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${profile?.businessName || 'poster'}-${Date.now()}.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Poster downloaded!');
      });
    } catch (error) {
      toast.error('Failed to export poster');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Loader2 className="animate-spin h-12 w-12 text-purple-600 mx-auto" />
            <p className="mt-4 text-slate-600">Loading Poster Creator...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <Sparkles className="inline w-8 h-8 mr-2 text-purple-600" />
                Digi Poster Creator
              </h1>
              <p className="text-slate-600">AI-Powered Social Media Posters with Your Brand Info</p>
            </div>
            <div className="flex gap-3">
              {profile && (
                <Button
                  onClick={() => setShowProfileModal(true)}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Profile
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* View Toggle */}
        {view !== 'editor' && (
          <div className="mb-6 flex gap-2">
            <Button
              onClick={() => setView('templates')}
              variant={view === 'templates' ? 'default' : 'outline'}
              className="flex items-center gap-2"
            >
              <Layout className="w-4 h-4" />
              Templates
            </Button>
            <Button
              onClick={() => setView('my-posters')}
              variant={view === 'my-posters' ? 'default' : 'outline'}
              className="flex items-center gap-2"
            >
              <Grid3x3 className="w-4 h-4" />
              My Posters ({myPosters.length})
            </Button>
          </div>
        )}

        {/* Templates View */}
        {view === 'templates' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {templates.map((template) => {
                const bgStyle = template.bgGradient 
                  ? `linear-gradient(135deg, ${template.bgGradient.from} 0%, ${template.bgGradient.to} 100%)`
                  : template.bgColor || 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)';
                
                const getOccasionEmoji = () => {
                  switch(template.category) {
                    case 'birthday': return '🎂';
                    case 'christmas': return '🎄';
                    case 'newyear': return '🎆';
                    case 'thanksgiving': return '🦃';
                    case 'valentine': return '💝';
                    case 'mothers': return '🌸';
                    case 'fathers': return '👔';
                    case 'easter': return '🐰';
                    case 'sale': return '🔥';
                    case 'opening': return '🎉';
                    default: return '✨';
                  }
                };
                
                return (
                  <Card
                    key={template.id}
                    className="cursor-pointer hover:shadow-lg transition-all overflow-hidden"
                    onClick={() => selectTemplate(template)}
                  >
                    <div 
                      className="relative" 
                      style={{ 
                        paddingBottom: `${(template.height / template.width) * 100}%`,
                        background: bgStyle
                      }}
                    >
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
                        <div className="text-5xl mb-3">{getOccasionEmoji()}</div>
                        <p className="font-bold text-white text-base mb-1">{template.name}</p>
                        <p className="text-white text-xs opacity-90">{profile?.businessName || 'Your Business'}</p>
                      </div>
                    </div>
                    <div className="p-3 border-t">
                      <p className="text-xs font-semibold text-slate-700">{template.name}</p>
                      {template.occasion && (
                        <p className="text-xs text-purple-600 mt-1">🎊 {template.occasion}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">{template.width} × {template.height}</p>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* My Posters View */}
        {view === 'my-posters' && (
          <div>
            {myPosters.length === 0 ? (
              <Card className="p-12 text-center">
                <ImageIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No posters yet</h3>
                <p className="text-slate-600 mb-6">Create your first poster from a template</p>
                <Button onClick={() => setView('templates')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Browse Templates
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {myPosters.map((poster) => (
                  <Card key={poster.id} className="overflow-hidden hover:shadow-lg transition-all">
                    <div className="relative pb-[100%] bg-gradient-to-br from-slate-100 to-slate-200">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <ImageIcon className="w-12 h-12 text-slate-400" />
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="font-semibold truncate">{poster.title}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {new Date(poster.createdAt).toLocaleDateString()}
                      </p>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" className="flex-1">
                          <Edit2 className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1">
                          <Download className="w-3 h-3 mr-1" />
                          Export
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Editor View */}
        {view === 'editor' && selectedTemplate && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <Button
                onClick={() => setView('templates')}
                variant="outline"
                size="sm"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back to Templates
              </Button>
              <div className="flex gap-2">
                <Button onClick={addTextElement} variant="outline" size="sm">
                  <Type className="w-4 h-4 mr-2" />
                  Add Text
                </Button>
                <Button onClick={() => setShowAIPanel(true)} variant="outline" size="sm">
                  <Wand2 className="w-4 h-4 mr-2" />
                  AI Content
                </Button>
                <Button onClick={() => setShowImageSearch(true)} variant="outline" size="sm">
                  <Search className="w-4 h-4 mr-2" />
                  Stock Images
                </Button>
                <Button onClick={savePoster} variant="outline" size="sm">
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
                <Button onClick={exportPoster} className="bg-purple-600 hover:bg-purple-700" size="sm">
                  <Download className="w-4 h-4 mr-2" />
                  Download PNG
                </Button>
              </div>
            </div>

            {/* Canvas */}
            <Card className="p-8 bg-slate-100">
              <div className="flex items-center justify-center">
                <div
                  ref={canvasRef}
                  className="relative shadow-2xl"
                  style={{
                    width: `${selectedTemplate.width}px`,
                    height: `${selectedTemplate.height}px`,
                    maxWidth: '100%',
                    transform: selectedTemplate.width > 800 ? 'scale(0.7)' : 'scale(1)',
                    transformOrigin: 'top center'
                  }}
                >
                  {canvasElements.map((element) => {
                    if (element.type === 'background') {
                      return (
                        <div
                          key={element.id}
                          className="absolute inset-0"
                          style={{
                            backgroundImage: element.image ? `url(${element.image})` : (
                              element.gradient
                                ? `linear-gradient(135deg, ${element.gradient.from} 0%, ${element.gradient.to} 100%)`
                                : 'none'
                            ),
                            backgroundColor: element.color,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center'
                          }}
                        />
                      );
                    }

                    if (element.type === 'overlay') {
                      return (
                        <div
                          key={element.id}
                          className="absolute inset-0"
                          style={{
                            backgroundColor: element.color
                          }}
                        />
                      );
                    }

                    if (element.type === 'text') {
                      return (
                        <div
                          key={element.id}
                          className={`absolute cursor-pointer ${selectedElement === element.id ? 'ring-2 ring-blue-500' : ''}`}
                          style={{
                            left: `${element.x}px`,
                            top: `${element.y}px`,
                            width: `${element.width}px`,
                            fontSize: `${element.fontSize}px`,
                            fontWeight: element.fontWeight || 'normal',
                            color: element.color,
                            textAlign: element.align || 'center',
                            wordWrap: 'break-word',
                            padding: '0 20px',
                            lineHeight: '1.2',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                          }}
                          onClick={() => setSelectedElement(element.id)}
                          onDoubleClick={() => setEditingText(element.id)}
                        >
                          {editingText === element.id ? (
                            <input
                              autoFocus
                              type="text"
                              value={element.content}
                              onChange={(e) => updateElementText(element.id, e.target.value)}
                              onBlur={() => setEditingText(null)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  setEditingText(null);
                                }
                              }}
                              className="w-full bg-white/30 border-2 border-blue-500 rounded px-3 py-2"
                              style={{
                                fontSize: `${Math.min(element.fontSize, 28)}px`,
                                fontWeight: element.fontWeight || 'normal',
                                color: element.color,
                                textAlign: element.align || 'center'
                              }}
                            />
                          ) : (
                            <span style={{ display: 'block', width: '100%' }}>{element.content}</span>
                          )}
                          {selectedElement === element.id && !editingText && (
                            <Button
                              size="sm"
                              variant="destructive"
                              className="absolute -top-10 right-2 z-10"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteElement(element.id);
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      );
                    }

                    if (element.type === 'image') {
                      return (
                        <div
                          key={element.id}
                          className={`absolute cursor-move ${selectedElement === element.id ? 'ring-2 ring-blue-500' : ''}`}
                          style={{
                            left: `${element.x}px`,
                            top: `${element.y}px`,
                            width: `${element.width}px`,
                            height: `${element.height}px`
                          }}
                          onClick={() => setSelectedElement(element.id)}
                        >
                          <img
                            src={element.url}
                            alt="Canvas element"
                            className="w-full h-full object-cover rounded-lg"
                          />
                          {selectedElement === element.id && (
                            <Button
                              size="sm"
                              variant="destructive"
                              className="absolute -top-8 -right-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteElement(element.id);
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      );
                    }

                    return null;
                  })}
                </div>
              </div>
            </Card>

            <Card className="p-4 bg-blue-50 border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>💡 Quick Tips:</strong> Double-click text to edit • Click elements to select • Click trash icon to delete • Use AI to generate content
              </p>
            </Card>
          </div>
        )}
      </div>

      {/* AI Content Panel Modal */}
      {showAIPanel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Wand2 className="w-6 h-6 text-purple-600" />
                  AI Content Generator
                </h2>
                <Button variant="outline" size="sm" onClick={() => setShowAIPanel(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>What do you want to create?</Label>
                  <Textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="E.g., Create a promotional post for 20% off sale this weekend"
                    rows={4}
                    className="mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Platform</Label>
                    <select
                      value={aiPlatform}
                      onChange={(e) => setAiPlatform(e.target.value)}
                      className="w-full mt-1 p-2 border rounded-md"
                    >
                      <option value="instagram">Instagram</option>
                      <option value="facebook">Facebook</option>
                      <option value="linkedin">LinkedIn</option>
                      <option value="twitter">Twitter/X</option>
                    </select>
                  </div>
                  <div>
                    <Label>Tone</Label>
                    <select
                      value={aiTone}
                      onChange={(e) => setAiTone(e.target.value)}
                      className="w-full mt-1 p-2 border rounded-md"
                    >
                      <option value="professional">Professional</option>
                      <option value="casual">Casual</option>
                      <option value="playful">Playful</option>
                      <option value="formal">Formal</option>
                    </select>
                  </div>
                </div>

                <Button
                  onClick={handleAIGenerate}
                  disabled={aiGenerating}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  {aiGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate & Add to Canvas
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Stock Image Search Modal */}
      {showImageSearch && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <Search className="w-6 h-6 text-purple-600" />
                  Stock Images
                </h2>
                <Button variant="outline" size="sm" onClick={() => setShowImageSearch(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex gap-2 mb-6">
                <Input
                  value={imageSearchQuery}
                  onChange={(e) => setImageSearchQuery(e.target.value)}
                  placeholder="Search for images..."
                  onKeyPress={(e) => e.key === 'Enter' && searchStockImages()}
                />
                <Button
                  onClick={searchStockImages}
                  disabled={searching}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {searchResults.map((img, index) => (
                  <div
                    key={index}
                    className="relative pb-[100%] rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity border-2 border-transparent hover:border-purple-500"
                    onClick={() => addImageToCanvas(img.url)}
                  >
                    <img
                      src={img.url}
                      alt={img.alt || 'Stock image'}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>

              {searchResults.length === 0 && !searching && (
                <div className="text-center py-12 text-slate-500">
                  <ImageIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>Search for images to add to your poster</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Business Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-4">Business Profile</h2>
              <p className="text-sm text-slate-600 mb-6">
                Your business info will be automatically added to poster templates
              </p>

              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Business Name *</Label>
                    <Input
                      value={profileForm.businessName}
                      onChange={(e) => setProfileForm({ ...profileForm, businessName: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Owner Name</Label>
                    <Input
                      value={profileForm.ownerName}
                      onChange={(e) => setProfileForm({ ...profileForm, ownerName: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={profileForm.email}
                      onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Location</Label>
                    <Input
                      value={profileForm.location}
                      onChange={(e) => setProfileForm({ ...profileForm, location: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input
                      value={profileForm.website}
                      onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Square Logo</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleLogoUpload(e, 'square')}
                        className="flex-1"
                      />
                      {profileForm.logoSquareUrl && (
                        <div className="w-12 h-12 border rounded">
                          <img
                            src={profileForm.logoSquareUrl}
                            alt="Square logo"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label>Round Logo</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleLogoUpload(e, 'round')}
                        className="flex-1"
                      />
                      {profileForm.logoRoundUrl && (
                        <div className="w-12 h-12 border rounded-full overflow-hidden">
                          <img
                            src={profileForm.logoRoundUrl}
                            alt="Round logo"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
                    <Check className="w-4 h-4 mr-2" />
                    Save Profile
                  </Button>
                  {profile && (
                    <Button type="button" variant="outline" onClick={() => setShowProfileModal(false)}>
                      Cancel
                    </Button>
                  )}
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DigiPosterCreator;